import React, { useState, useCallback } from 'react';
import cn from 'clsx';
import {
  ArmchairIcon,
  CarIcon,
  ClockCounterClockwiseIcon,
  CoffeeIcon,
  MapPinLineIcon,
} from '@fronton/icons-react';
import { Layout } from 'fronton-react';
import { COMMON_LANDINGS_IMAGES_CDN_PATH } from 'common/constants/common';
import { createElementKey } from 'utils';
import { ButtonWithGAClickButton } from '../button';
import { GalleryImage } from '../gallery';
import { GalleryModal } from '../gallery-modal';
import { Section } from '../section';
import { SectionSubTitle } from '../section-subtitle';
import { Typography } from '../typography';
import {
  signSection,
  signSectionStoreGrid,
  signSectionStoreImage,
  signSectionStoreItem,
  signSectionPoint,
  signSectionPointsWrapper,
  signSectionSubtitle,
  signSectionTitle,
  signSectionBlockTextWrapper,
  signSectionBlockWrapper,
  signSectionContainer,
  signSectionPointsOnlineWrapper,
  signSectionOnlineImage,
  signSectionAddContentContainer,
} from './sign-section-styles';

type SignSectionProps = {
  className?: string;
  elementBlock: string;
  elementName: string;
  galleryList: GalleryImage[]
  signHref: string;
  renderTitle: (titleClassName: string) => React.ReactNode;
  renderAddContent?: () => React.ReactNode;
};

export const SignSection: React.FC<SignSectionProps> = ({
  className,
  elementBlock,
  elementName,
  galleryList,
  signHref,
  renderTitle,
  renderAddContent,
}) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const handleImageClick = (imageIndex: number) => () => {
    setActiveImageIndex(imageIndex);
    setIsModalOpen(true);
  };

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setActiveImageIndex(0);
  }, [setIsModalOpen, setActiveImageIndex]);

  return (
    <Section className={cn(className, signSection)}>
      <Layout>
        <div className={signSectionContainer}>
          <div className={signSectionBlockWrapper}>
            {renderTitle(signSectionTitle)}
            <Typography
              className={signSectionSubtitle}
              variant="l"
              size="body_long"
              color="text-invert">
              Запишитесь заранее, {'\n'}чтобы мы&nbsp;вас встретили и&nbsp;всё показали
            </Typography>
            <div className={signSectionStoreGrid}>
              {galleryList.map((image, index) => (
                <button
                  className={signSectionStoreItem}
                  key={createElementKey('sign-image', index)}
                  onClick={handleImageClick(index)}>
                  <img
                    className={signSectionStoreImage}
                    src={image.srcSmall}
                    alt={image.alt}
                    width="145"
                    height="145"
                  />
                </button>
              ))}
            </div>
            <div className={signSectionPointsWrapper}>
              <div className={signSectionPoint}>
                <CarIcon size="38px" color="var(--control-accent)" type="duotone" />
                <Typography variant="l" size="body_accent" color="text-invert">
                  Парковка
                </Typography>
              </div>
              <div className={signSectionPoint}>
                <CoffeeIcon size="38px" color="var(--control-accent)" type="duotone" />
                <Typography variant="l" size="body_accent" color="text-invert">
                  Кафе
                </Typography>
              </div>
              <div className={signSectionPoint}>
                <ArmchairIcon size="38px" color="var(--control-accent)" type="duotone" />
                <Typography variant="l" size="body_accent" color="text-invert">
                  Зона отдыха
                </Typography>
              </div>
            </div>
          </div>
          <div className={signSectionBlockWrapper}>
            <div className={signSectionBlockTextWrapper}>
              <SectionSubTitle className={signSectionTitle} margin="s" color="text-invert">
                Создать или завершить проект с&nbsp;дизайнером можно онлайн
              </SectionSubTitle>
              <Typography
                className={signSectionSubtitle}
                variant="l"
                size="body_long"
                color="text-invert">
                Общайтесь в&nbsp;мессенджере или&nbsp;в&nbsp;зуме&nbsp;&mdash; как&nbsp;вам удобно
              </Typography>
            </div>
            <img
              className={signSectionOnlineImage}
              src={`${COMMON_LANDINGS_IMAGES_CDN_PATH}/sign-section/online.png/online.png`}
              alt="Создание проекта онлайн"
              width="578"
              height="302"
            />
            <div
              className={cn(signSectionPointsWrapper, signSectionPointsOnlineWrapper)}>
              <div className={signSectionPoint}>
                <ClockCounterClockwiseIcon size="38px" color="var(--control-accent)" type="duotone" />
                <Typography variant="l" size="body_accent" color="text-invert">
                  В&nbsp;любое время
                </Typography>
              </div>
              <div className={signSectionPoint}>
                <MapPinLineIcon size="38px" color="var(--control-accent)" type="duotone" />
                <Typography variant="l" size="body_accent" color="text-invert">
                  В&nbsp;любом месте
                </Typography>
              </div>
            </div>
          </div>
        </div>

        {renderAddContent && (
          <div className={signSectionAddContentContainer}>
            {renderAddContent()}
          </div>
        )}
      </Layout>

      <GalleryModal
        clickedImageIndex={activeImageIndex}
        elementBlock={elementBlock}
        elementText={`${elementName} / modal`}
        galleryList={galleryList}
        onClose={handleCloseModal}
        renderFooterButton={() => (
          <ButtonWithGAClickButton
            elementBlock={elementBlock}
            elementName={`${elementName} / modal`}
            elementText="Записаться на проектирование"
            invert
            variant="accent"
            href={signHref}
            target="_blank"
            onClick={handleCloseModal}>
            Записаться на проектирование
          </ButtonWithGAClickButton>
        )}
        show={isModalOpen}
      />
    </Section>
  );
};
